import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AdminReports.css';
import { Circles } from 'react-loader-spinner';
import privateAxios from '../../utils/axios/privateAxios';
import { FaEdit, FaTrash } from 'react-icons/fa';
import moment from 'moment';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';

const AdminReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: '',
    onConfirm: null,
    loading: false,
  });

  const fetchReports = async (page = 1) => {
    try {
      setLoading(true);
      const response = await privateAxios.get(`/private/admin-reports?page=${page}`);
      setReports(response.data.reports);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching reports', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchedReports = async (page = 1) => {
    try {
      setLoading(true);
      const response = await privateAxios.get(`/private/admin-search-reports?query=${searchTerm.trim()}&page=${page}`);
      setReports(response.data.reports);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error searching reports', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setSearchMode(false);
      fetchReports(1);
    } else {
      setSearchMode(true);
      fetchSearchedReports(1);
    }
  };

  const handleCheckbox = (id) => {
    setSelectedReports((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const deleteSelectedReports = async () => {
    if (!selectedReports.length) return;
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete ${selectedReports.length} selected report(s)?`,
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          await privateAxios.post('/private/delete-selected-reports', { ids: selectedReports });
          setSelectedReports([]);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          searchMode ? fetchSearchedReports(currentPage) : fetchReports(currentPage);
        } catch (error) {
          console.error('Error deleting reports', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  const handleDeleteSingle = (id, title) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete the report titled "${title}"?`,
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          await privateAxios.delete(`/private/delete-report/${id}`);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          searchMode ? fetchSearchedReports(currentPage) : fetchReports(currentPage);
        } catch (error) {
          console.error('Error deleting report', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  const handleEdit = (id) => navigate(`/admin-edit-report/${id}`);

  const handleClickReport = (id, e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    navigate(`/admin-report/${id}`);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      searchMode ? fetchSearchedReports(nextPage) : fetchReports(nextPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      searchMode ? fetchSearchedReports(prevPage) : fetchReports(prevPage);
    }
  };

  return (
    <div className="admin-reports">
      <div className="admin-reports-header">
        <Link to={'/create-report'} className='admin-reports-create-report'>Create Report</Link>
        <div className='admin-reports-search-input'>
          <input
            type="text"
            placeholder="Search reports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className='admin-reports-search-btn' onClick={handleSearch}>Search</button>
        </div>

        <button
          className="admin-reports-delete-selected-btn"
          style={{ backgroundColor: selectedReports.length ? '#008751' : '#ccf1e4' }}
          onClick={deleteSelectedReports}
          disabled={!selectedReports.length}
        >
          Delete Selected
        </button>
      </div>

      <div className="admin-reports-list">
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', columnGap: '10px', alignItems: 'center', justifyContent: 'center', height: '300px', color: 'gray' }}>
            <Circles height="50" width="50" color="#008751" ariaLabel="loading" /> <h2>Loading Reports...</h2>
          </div>
        ) : reports.length ? (
          reports.map((r) => (
            <div className="admin-reports-card" key={r._id} onClick={(e) => handleClickReport(r.slug, e)}>
              <div className='admin-reports-checkbox-title'>
                <input
                  type="checkbox"
                  checked={selectedReports.includes(r._id)}
                  onChange={() => handleCheckbox(r._id)}
                />
                <div className="admin-reports-info">
                  <h4>
                    {r.title.split(' ').length > 10
                      ? r.title.split(' ').slice(0, 10).join(' ') + '...'
                      : r.title}
                  </h4>

                  <small>
                    <p>Posted By: <span style={{ fontWeight: "bold" }}>{r.createdBy?.firstname} {r.createdBy?.lastname}</span> </p>
                    <p>On: <span style={{ fontWeight: "bold" }}>{moment(r.createdAt).format('MMMM DD, YYYY')}</span></p>
                    {r.updatedAt && ` | Last Edited: ${new Date(r.updatedAt).toLocaleDateString()}`}
                  </small>
                </div>
              </div>

              <div className="admin-reports-actions">
                <button className='admin-reports-edit-button' onClick={() => handleEdit(r.slug)}><FaEdit /></button>
                <button
                  className='admin-reports-delete-button'
                  onClick={() => handleDeleteSingle(r._id, r.title)}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div>No reports found.</div>
        )}
      </div>

      {reports.length > 10 &&
        <div className="admin-reports-pagination-controls">
          <button onClick={handlePreviousPage} disabled={currentPage === 1}>Previous</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
        </div>}

      <div className="admin-reports-back-to-admin-dashboard">
        <Link to={'/admin-dashboard'}>Back to Management Dashboard</Link>
      </div>

      {confirmDialog.open && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false })}
          loading={confirmDialog.loading}
        />
      )}
    </div>
  );
};

export default AdminReports;